import { useEffect, useMemo, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, Filter, X, Download, Wallet, FileText,
  CheckCircle2, XCircle, RotateCcw, Sparkles, Building2, ExternalLink, Save, Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = [
  { key: "open", label: "Aberto", color: "bg-neutral-100 text-neutral-700" },
  { key: "closed", label: "Fechado", color: "bg-blue-50 text-blue-700" },
  { key: "paid", label: "Pago", color: "bg-emerald-50 text-emerald-700" },
  { key: "cancelled", label: "Cancelado", color: "bg-red-50 text-red-700" },
];
const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const brl = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const StatusBadge = ({ status }) => {
  const s = STATUSES.find((x) => x.key === status) || { label: status, color: "bg-neutral-100 text-neutral-700" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${s.color}`}>{s.label}</span>;
};

export default function Billing() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [agencyF, setAgencyF] = useState("");
  const [yearF, setYearF] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const canManage = ["admin_geral","administrativo","financeiro"].includes(user?.role);
  const isImob = ["imobiliaria_admin","imobiliaria_user"].includes(user?.role);

  const load = useCallback(async () => {
    const params = {};
    if (statusF) params.status = statusF;
    if (agencyF && !isImob) params.agency_id = agencyF;
    if (yearF) params.year = Number(yearF);
    try {
      const { data } = await api.get("/billing/periods", { params });
      setItems(data);
    } catch (e) { toast.error(extractError(e)); }
  }, [statusF, agencyF, yearF, isImob]);

  useEffect(() => { if (!isImob) api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {}); }, [isImob]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    try { const { data } = await api.get(`/billing/periods/${row.id}`); setDetail(data); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Fase C · Financeiro</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">Faturamento</h1>
          <p className="text-sm text-[#6B7280] mt-1">Fechamento mensal por imobiliária com fatura em PDF.</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowNew(true)} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="billing-new-btn">
            <Sparkles size={16} className="mr-2" /> Gerar fechamento
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Filter size={14} className="text-[#6B7280]" />
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="bg-white border border-[#E5E7EB] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none" data-testid="billing-filter-status">
          <option value="">Todos os status</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {!isImob && (
          <select value={agencyF} onChange={(e) => setAgencyF(e.target.value)} className="bg-white border border-[#E5E7EB] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none" data-testid="billing-filter-agency">
            <option value="">Todas as imobiliárias</option>
            {agencies.map((a) => <option key={a.id} value={a.id}>{a.trade_name}</option>)}
          </select>
        )}
        <Input type="number" placeholder="Ano" value={yearF} onChange={(e) => setYearF(e.target.value)} className="w-24 bg-white text-xs" data-testid="billing-filter-year" />
        {(statusF || agencyF || yearF) && (
          <button onClick={() => { setStatusF(""); setAgencyF(""); setYearF(""); }} className="text-xs text-[#6B7280] hover:text-[#0B1F3A] flex items-center gap-1">
            <X size={12} /> Limpar
          </button>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="px-4 py-3 font-bold">Nº Fatura</th>
              <th className="px-4 py-3 font-bold">Imobiliária</th>
              <th className="px-4 py-3 font-bold">Período</th>
              <th className="px-4 py-3 font-bold text-right">Vistorias</th>
              <th className="px-4 py-3 font-bold text-right">Total</th>
              <th className="px-4 py-3 font-bold">Vencimento</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p)} className="border-b border-[#F1F2F4] hover:bg-[#F9FAFB] cursor-pointer" data-testid={`billing-row-${p.id}`}>
                <td className="px-4 py-3 font-mono-data text-xs text-[#0B1F3A] font-semibold">{p.invoice_number}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0A0A0A]">{p.agency_name}</div>
                </td>
                <td className="px-4 py-3 text-xs">{MONTHS[p.month - 1]} / {p.year}</td>
                <td className="px-4 py-3 text-right font-mono-data">{(p.items || []).length}</td>
                <td className="px-4 py-3 text-right font-mono-data font-semibold text-[#0B1F3A]">{brl(p.total)}</td>
                <td className="px-4 py-3 text-xs font-mono-data">{p.due_date}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-right">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/periods/${p.id}/invoice.pdf`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#6B7280] hover:text-[#0B1F3A]" title="Baixar PDF" data-testid={`billing-pdf-${p.id}`}>
                    <Download size={14} />
                  </a>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#9CA3AF] text-sm">Nenhum fechamento encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && canManage && (
        <GenerateDialog open={showNew} onClose={() => setShowNew(false)} onCreated={load} agencies={agencies} />
      )}
      {detail && (
        <BillingDetailDialog open={!!detail} data={detail} onClose={() => setDetail(null)} onChanged={(d) => { setDetail(d); load(); }} canManage={canManage} />
      )}
    </div>
  );
}

/* ---------- Generate Dialog ---------- */
function GenerateDialog({ open, onClose, onCreated, agencies }) {
  const now = new Date();
  const [agency, setAgency] = useState("");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [unitPrice, setUnitPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedAgency = agencies.find((a) => a.id === agency);

  useEffect(() => {
    if (selectedAgency && !unitPrice) setUnitPrice(selectedAgency.min_value || "");
  }, [selectedAgency, unitPrice]);

  const submit = async () => {
    if (!agency) { toast.error("Selecione uma imobiliária"); return; }
    setSaving(true);
    try {
      const payload = { agency_id: agency, year: Number(year), month: Number(month) };
      if (unitPrice) payload.unit_price = Number(unitPrice);
      const { data } = await api.post("/billing/periods/generate", payload);
      toast.success(`Fechamento ${data.invoice_number} gerado`);
      onCreated(); onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0B1F3A]">Gerar fechamento</DialogTitle>
          <DialogDescription className="text-[#6B7280]">Selecione imobiliária e mês. Serão incluídas todas as vistorias concluídas no período.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Imobiliária</Label>
            <select value={agency} onChange={(e) => setAgency(e.target.value)} className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none" data-testid="billing-gen-agency">
              <option value="">— selecione —</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.trade_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Mês</Label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none" data-testid="billing-gen-month">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label>Ano</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="billing-gen-year" />
            </div>
          </div>
          <div>
            <Label>Valor por vistoria (R$)</Label>
            <Input type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder={selectedAgency ? `Padrão: ${brl(selectedAgency.min_value)}` : ""} data-testid="billing-gen-price" />
            <p className="text-[10px] text-[#6B7280] mt-1">Se vazio, usa o valor mínimo cadastrado na imobiliária.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="billing-gen-submit">
            {saving ? "Gerando…" : "Gerar fechamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Detail Dialog ---------- */
function BillingDetailDialog({ open, data, onClose, onChanged, canManage }) {
  const [adjs, setAdjs] = useState(data.adjustments || []);
  const [notes, setNotes] = useState(data.notes || "");
  const [dueDate, setDueDate] = useState(data.due_date || "");
  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const adjTotal = useMemo(() => adjs.reduce((s, a) => s + Number(a.amount || 0), 0), [adjs]);
  const total = (data.subtotal || 0) + adjTotal;

  const isCancelled = data.status === "cancelled";
  const isPaid = data.status === "paid";
  const isClosed = data.status === "closed";

  const addAdj = () => {
    if (!newLabel.trim()) { toast.error("Descrição do ajuste é obrigatória"); return; }
    const amt = Number(newAmount);
    if (Number.isNaN(amt)) { toast.error("Valor inválido"); return; }
    setAdjs((a) => [...a, { label: newLabel, amount: amt }]);
    setNewLabel(""); setNewAmount("");
  };
  const removeAdj = (i) => setAdjs((a) => a.filter((_, ix) => ix !== i));

  const save = async (extraPayload = {}) => {
    setSaving(true);
    try {
      const payload = { adjustments: adjs, notes, due_date: dueDate, ...extraPayload };
      const { data: fresh } = await api.patch(`/billing/periods/${data.id}`, payload);
      onChanged(fresh);
      toast.success("Fechamento atualizado");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const transition = async (status) => {
    if (status === "cancelled" && !window.confirm("Cancelar este fechamento? A ação é irreversível.")) return;
    await save({ status });
  };

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/billing/periods/${data.id}/invoice.pdf`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="billing-detail">
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <DialogTitle className="font-display text-[#0B1F3A]">{data.invoice_number}</DialogTitle>
            <StatusBadge status={data.status} />
          </div>
          <DialogDescription className="text-[#6B7280]">
            {data.agency_name} · {MONTHS[data.month - 1]} / {data.year} · Vencimento {data.due_date}
          </DialogDescription>
        </DialogHeader>

        {/* Items */}
        <div className="border border-[#E5E7EB] rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[#0B1F3A]">
                <th className="px-3 py-2 font-bold">Data</th>
                <th className="px-3 py-2 font-bold">Endereço</th>
                <th className="px-3 py-2 font-bold">Cidade</th>
                <th className="px-3 py-2 font-bold">Tipo</th>
                <th className="px-3 py-2 font-bold text-right">Área</th>
                <th className="px-3 py-2 font-bold text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((it) => (
                <tr key={it.id} className="border-b border-[#F1F2F4]" data-testid={`billing-item-${it.id}`}>
                  <td className="px-3 py-2 font-mono-data">{it.scheduled_date}</td>
                  <td className="px-3 py-2">{it.address || "—"}</td>
                  <td className="px-3 py-2">{it.city_name}</td>
                  <td className="px-3 py-2 capitalize">{it.inspection_type}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{it.area_m2} m²</td>
                  <td className="px-3 py-2 text-right font-mono-data font-semibold">{brl(it.amount)}</td>
                </tr>
              ))}
              {(data.items || []).length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#9CA3AF]">Nenhuma vistoria concluída no período.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Adjustments */}
        <div className="border border-[#E5E7EB] rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">Ajustes</div>
          </div>
          <ul className="space-y-1 mb-2">
            {adjs.map((a, i) => (
              <li key={i} className="flex items-center justify-between border border-[#E5E7EB] rounded px-2 py-1 text-xs" data-testid={`billing-adj-${i}`}>
                <span>{a.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`font-mono-data font-semibold ${a.amount < 0 ? "text-red-600" : "text-[#0B1F3A]"}`}>{brl(a.amount)}</span>
                  {canManage && !isCancelled && (
                    <button onClick={() => removeAdj(i)} className="text-[#6B7280] hover:text-red-600" data-testid={`billing-adj-remove-${i}`}><Trash2 size={12} /></button>
                  )}
                </div>
              </li>
            ))}
            {adjs.length === 0 && <li className="text-xs text-[#9CA3AF]">Nenhum ajuste.</li>}
          </ul>
          {canManage && !isCancelled && (
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
              <div className="sm:col-span-3">
                <Label>Descrição</Label>
                <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Ex.: Desconto trimestral" data-testid="billing-adj-label" />
              </div>
              <div>
                <Label>Valor (R$)</Label>
                <Input type="number" step="0.01" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="Use negativo para desconto" data-testid="billing-adj-amount" />
              </div>
              <Button onClick={addAdj} variant="outline" className="border-[#E5E7EB] text-[#0A0A0A]" data-testid="billing-adj-add"><Plus size={14} className="mr-1" /> Adicionar</Button>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="border border-[#E5E7EB] rounded p-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#6B7280]">Subtotal</span><span className="font-mono-data">{brl(data.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#6B7280]">Ajustes</span><span className={`font-mono-data ${adjTotal < 0 ? "text-red-600" : ""}`}>{brl(adjTotal)}</span></div>
          <div className="flex justify-between border-t border-[#E5E7EB] pt-2 mt-2 items-center">
            <span className="font-bold text-[#0B1F3A]">TOTAL</span>
            <span className="font-mono-data font-bold text-xl text-[#0B1F3A] bg-[#FFD400] px-3 py-0.5 rounded" data-testid="billing-total">{brl(total)}</span>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Vencimento</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={!canManage || isCancelled} data-testid="billing-due-date" />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage || isCancelled} data-testid="billing-notes" />
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm hover:border-[#0B1F3A]" data-testid="billing-pdf-btn">
            <Download size={14} /> Baixar PDF
          </a>
          {canManage && !isCancelled && (
            <Button onClick={() => save()} disabled={saving} variant="outline" className="border-[#E5E7EB] text-[#0A0A0A]" data-testid="billing-save-btn">
              <Save size={14} className="mr-1" /> {saving ? "Salvando…" : "Salvar"}
            </Button>
          )}
          {canManage && data.status === "open" && (
            <Button onClick={() => transition("closed")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="billing-close-btn">
              <CheckCircle2 size={14} className="mr-1" /> Fechar (emitir)
            </Button>
          )}
          {canManage && isClosed && (
            <>
              <Button onClick={() => transition("open")} variant="outline" className="border-[#E5E7EB] text-[#0A0A0A]"><RotateCcw size={14} className="mr-1" /> Reabrir</Button>
              <Button onClick={() => transition("paid")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="billing-pay-btn"><Wallet size={14} className="mr-1" /> Marcar pago</Button>
            </>
          )}
          {canManage && !isCancelled && !isPaid && (
            <Button onClick={() => transition("cancelled")} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="billing-cancel-btn">
              <XCircle size={14} className="mr-1" /> Cancelar
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
